import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { writeBatch, setDoc, deleteDoc, serverTimestamp } from './writes';
import { ref as storageRef, uploadBytesResumable, deleteObject } from 'firebase/storage';
import { docRef, COLLECTIONS } from './paths';
import { db, storage } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { callable } from './useEntitlement';
import { createFileLinks, linksFromCallable } from './fileLinks';
import { useCollectionWindow } from './useCollectionWindow';
import { todayISO } from '../domain/clock';
import { freeId } from '../domain/freeId';
import {
  deleteFolderRefusal,
  folderDocument,
  folderMoveDocument,
  folderNameRefusal,
  folderRenameDocument,
  moveFileRefusal,
  moveFolderRefusal,
  resourceMoveDocument,
  resourceNameRefusal,
  resourceRenameDocument,
  resourceRowDocument,
} from '../domain/resources';
import {
  FileRefused,
  NOT_RECORDED,
  NOT_REMOVED,
  UPLOAD_FAILED,
  isRemoved,
  uploadType,
  refuseFile,
  removedOn,
  uploadErrorMessage,
} from '../lib/fileLimits';
import { UploadCancelled, watchUpload } from '../lib/uploadWatch';

// The studio's files: the Online Resources library (#424). Uploading one into
// a folder, renaming and moving it, the folders themselves, a link to one, and
// taking one away.
//
// ── What the library is ─────────────────────────────────────────────────────
//
// MyMusicStaff's Online Resources: a folder tree under Home that a teacher
// curates once and links from as many lessons as she likes. The rules of the
// tree — how it is drawn when two offline devices disagree about it, what may
// be linked, what a link to a deleted file reads as — are core/src/resources.rs;
// this is the provider that reads the two collections and writes them.
//
// **A provider (#424)**: the library page and the attendance drawer both read
// the rows, from one subscription.
//
// ── Where they live, and how one is opened ──────────────────────────────────
//
// `files/{uid}/{cap}/{name}` — a **top level of its own**, not a folder under
// `users/`, for the lifecycle reasons in functions/files/paths.js.
//
// **Opened through a link that expires (#431).** A file is opened the way a
// receipt is: through `linkFor(path)`, a signed link the `fileLinks` callable
// mints for this studio, good for thirty days (#497), with the expiry in the
// URL (src/data/fileLinks.js, functions/files/links.js). `copyLink` hands that
// same link to the teacher (#515): the address a viewer's tab ends up on is the
// hour-long storage URL the link redirects to, which is not one to send.
//
// `{cap}` keeps its own segment and its 160 bits: it is what makes one file
// re-keyable, and a URL that guesses its way to a path still needs a signature
// to open it.
//
// ── The ordering, and why a callable does not break offline-first ───────────
//
// Authorise, upload, then write the row — the callable first because the path
// does not exist until the server mints the cap. As in src/data/useExpenses.jsx,
// the upload precedes the document, so the path is known when the row is
// written. Storage has no local write queue, so an upload already needs a
// connection and the callable adds a round trip to a path that is never
// offline. Everything after the upload — a rename, a move, a folder — is an
// ordinary queued write and works offline.
//
// ── The class the rows are written under ────────────────────────────────────
//
// **Per-field merge** (#424, core/src/reconcile.rs). `uploadFile` creates a row
// whole; a rename and a move each merge the one field they change, so a rename
// on one device and a move on another both land. `removedOn` is written only
// by the lapse sweep, over the admin SDK.

// The row an upload writes is `resourceRowDocument` (the core's
// `resource_row_document`): `file_row_document` and the folder. A contract:
// firestore.rules validates this shape and refuses a row whose `path` is not
// under the writer's own prefix, or whose `path` changes after it is written.
//
// `removedOn` is present and null, not absent: firestore.rules requires it,
// and the sweep that stamps it later finds rows with
// `where('removedOn', '==', null)` — which matches nothing on a document that
// lacks the field at all. A row written without it would never be swept and
// would keep pointing at an object that had been deleted.

const FilesContext = createContext(null);

// A library is larger than a managed list and much smaller than attendance.
// The same bound the rules put on any list read (`withinListBound`).
const TRACK_LIMIT = 5000;
const FOLDER_LIMIT = 5000;

export function FilesProvider({ children }) {
  const { user } = useAuth();
  const uid = user?.uid || null;
  // The Region's decimal separator, for the sizes in a refusal (#583).
  const { decimalSeparator } = useLocale();

  const fileRead = useCollectionWindow({ uid, collection: COLLECTIONS.files, limit: TRACK_LIMIT });
  const folderRead = useCollectionWindow({ uid, collection: COLLECTIONS.resourceFolders, limit: FOLDER_LIMIT });
  const files = fileRead.docs;
  const folders = folderRead.docs;

  // This studio's file links, held while they are good. A new uid is a new
  // cache, so a link minted for one studio is never handed to another.
  const links = useMemo(
    () => createFileLinks({ fetchLinks: linksFromCallable(uid ? callable('fileLinks') : null) }),
    [uid],
  );

  // Authorise, upload, record. Throws a `FileRefused` whose message is a
  // sentence written for a teacher — every path out of here is named (#62).
  //
  // `onProgress({ transferred, total })` is called as the bytes go up (#577):
  // a large recording takes minutes on a home connection.
  //
  // The bytes go up under the no-progress watchdog (src/lib/uploadWatch.js,
  // #612): a connection that goes silent fails in 45 s, plus the time the
  // chunk under way would take on a slow line, with the timeout sentence.
  //
  // Aborting `signal` cancels the upload and rejects with `UploadCancelled`.
  // Bytes that land anyway — the cancel raced the last chunk — are deleted
  // and no row is written, so a cancelled file is not in the library.
  const uploadFile = useCallback(async (file, { folderId = null, onProgress = null, signal = null } = {}) => {
    if (!uid || !storage) throw new FileRefused(UPLOAD_FAILED);

    // Before any bytes move. The server refuses the same two things and
    // storage.rules refuses them again at the object; this one exists so a
    // phone connection is not spent finding out.
    const refusal = refuseFile(file, decimalSeparator);
    if (refusal) throw new FileRefused(refusal);

    const authorizeUpload = callable('authorizeUpload');
    if (!authorizeUpload) throw new FileRefused('File uploads are not switched on yet.');

    // The content type as the rule will see it, decided once and used for both
    // the authorisation and the object, so the callable and the bucket judge
    // the same type. Named by the file's extension when the browser could not
    // type it (#133).
    const contentType = uploadType(file);

    let grant;
    try {
      const res = await authorizeUpload({ name: file.name || '', contentType, size: file.size });
      grant = res?.data;
    } catch (err) {
      throw new FileRefused(uploadErrorMessage(err));
    }
    if (!grant?.path) throw new FileRefused(UPLOAD_FAILED);

    // `cacheControl` comes back from the server rather than being read from the
    // client's own mirror: the header is the product's promise about how long a
    // deleted file keeps being served, so the value that ends up on the object
    // should be the one the server decided.
    //
    // Resumable rather than `uploadBytes` (#577): it reports progress, and it
    // sends a large file in chunks the SDK retries one at a time, where
    // `uploadBytes` sends one request that a dropped connection restarts from
    // nothing.
    if (signal?.aborted) throw new UploadCancelled();
    const task = uploadBytesResumable(storageRef(storage, grant.path), file, {
      contentType,
      cacheControl: grant.cacheControl,
    });
    await watchUpload(task, { total: file.size || 0, onProgress, signal });
    if (signal?.aborted) {
      await deleteObject(storageRef(storage, grant.path)).then(null, (err) => {
        console.warn('[files] a cancelled upload landed and could not be deleted', err);
      });
      throw new UploadCancelled();
    }

    // The server's day when it said one (functions/index.js returns it so the
    // row carries it, and so a browser with a wrong clock does not decide what
    // day a studio's upload happened on), and this device's otherwise. A
    // record of when the file arrived and nothing more: no expiry is computed
    // from it (#86).
    const row = resourceRowDocument({
      name: file.name || 'file',
      contentType,
      size: file.size || 0,
      path: grant.path,
      uploadedOn: grant.uploadedOn || todayISO(),
      folderId,
    });

    // A fresh id, **not** the cap. The cap is the capability and it is meant to
    // be rotatable: re-keying a file copies the object to a new cap and updates
    // this row's `path` (server-side), which only works if the row's identity
    // is something else — and it is what lessons link to.
    const id = freeId('file');
    try {
      const batch = writeBatch(db);
      batch.set(docRef(uid, COLLECTIONS.files, id), row);
      await batch.commit();
    } catch (err) {
      // The object is up and the row is not: an orphan the byte counter can
      // see and the teacher cannot. Said rather than swallowed, and said in its
      // own words — the counter is trigger-maintained from the bucket, so those
      // bytes are genuinely spent and "could not be uploaded" would be false.
      console.warn('[files] object stored but the row was refused', err);
      throw new FileRefused(NOT_RECORDED);
    }

    return { id, ...row };
  }, [uid, decimalSeparator]);

  // Object first, row second — the opposite order to useExpenses' removeEntry,
  // where the entry goes first and the image is best-effort. Here the row is
  // the only handle on the object's path: deleting the row first would orphan
  // the object until the studio lapses and the sweep walks the whole prefix.
  //
  // So a delete that cannot remove the object stops, and the teacher can try
  // again; `object-not-found` counts as gone, so retrying is safe.
  //
  // **Lessons that link the file are not touched.** Their `resourceIds` keep
  // the id, and every surface reads it as "no longer in your library"
  // (`linked_resources` in core/src/resources.rs); the notes email leaves it
  // out; a link already sent opens `openFileLink`'s "no longer available"
  // page. No fan-out over the studio's attendance history.
  const removeFile = useCallback(async (row) => {
    if (!uid || !row?.id) return;
    if (row.path && storage && !isRemoved(row)) {
      try {
        await deleteObject(storageRef(storage, row.path));
      } catch (err) {
        if (err?.code !== 'storage/object-not-found') {
          console.warn('[files] object delete failed', err);
          throw new FileRefused(NOT_REMOVED);
        }
      }
      links.forget(row.path);
    }
    try {
      const batch = writeBatch(db);
      batch.delete(docRef(uid, COLLECTIONS.files, row.id));
      await batch.commit();
    } catch (err) {
      // Through a sentence rather than the SDK's: a raw FirebaseError names a
      // document path the teacher has never seen.
      console.warn('[files] row delete failed', err);
      throw new FileRefused(NOT_REMOVED);
    }
  }, [uid, links]);

  // A rename and a move are one field each, merged (per-field merge, above).
  // Each refuses with the core's sentence before anything is queued.
  const renameFile = useCallback(async (id, name) => {
    if (!uid || !id) return;
    const refusal = resourceNameRefusal(name);
    if (refusal) throw new FileRefused(refusal.message);
    await setDoc(docRef(uid, COLLECTIONS.files, id), resourceRenameDocument(name), { merge: true });
  }, [uid]);

  const moveFile = useCallback(async (id, folderId) => {
    if (!uid || !id) return;
    const refusal = moveFileRefusal(folders, folderId);
    if (refusal) throw new FileRefused(refusal.message);
    await setDoc(docRef(uid, COLLECTIONS.files, id), resourceMoveDocument(folderId), { merge: true });
  }, [uid, folders]);

  // ── Folders ──
  //
  // Free ids: a folder is something one teacher made, not a decision two
  // devices reach, so two offline "Scales" are two folders — the same answer
  // categories and note templates give, and the same cross-document
  // uniqueness that core/src/reconcile.rs says no document rule can see.
  const createFolder = useCallback(async (name, parentId = null) => {
    if (!uid) return null;
    const refusal = folderNameRefusal(name);
    if (refusal) throw new FileRefused(refusal.message);
    const id = freeId('fld');
    await setDoc(docRef(uid, COLLECTIONS.resourceFolders, id), folderDocument(name, parentId, serverTimestamp()));
    return id;
  }, [uid]);

  const renameFolder = useCallback(async (id, name) => {
    if (!uid || !id) return;
    const refusal = folderNameRefusal(name);
    if (refusal) throw new FileRefused(refusal.message);
    await setDoc(docRef(uid, COLLECTIONS.resourceFolders, id), folderRenameDocument(name), { merge: true });
  }, [uid]);

  const moveFolder = useCallback(async (id, parentId) => {
    if (!uid || !id) return;
    const refusal = moveFolderRefusal(folders, id, parentId);
    if (refusal) throw new FileRefused(refusal.message);
    await setDoc(docRef(uid, COLLECTIONS.resourceFolders, id), folderMoveDocument(parentId), { merge: true });
  }, [uid, folders]);

  // Only an empty folder (`delete_folder_refusal`). Asked of the rows this
  // device holds, so an offline device can still delete a folder another has
  // since filled; what was put in it then shows at Home rather than being lost.
  const removeFolder = useCallback(async (id) => {
    if (!uid || !id) return;
    const refusal = deleteFolderRefusal(folders, files, id);
    if (refusal) throw new FileRefused(refusal.message);
    await deleteDoc(docRef(uid, COLLECTIONS.resourceFolders, id));
  }, [uid, folders, files]);

  return (
    <FilesContext.Provider
      value={useMemo(() => ({
        files,
        folders,
        loaded: fileRead.hasRead && folderRead.hasRead,
        // Either read stopped before it answered (#647).
        failed: fileRead.failed || folderRead.failed,
        uploadFile,
        removeFile,
        renameFile,
        moveFile,
        createFolder,
        renameFolder,
        moveFolder,
        removeFolder,
        // A promise of a link that is good for at least the renewal margin.
        // Never stored on the row: the row holds the path, and the link is
        // derived.
        linkFor: (path) => links.linkFor(path),
        // Whether the sweep has taken it, and what to say instead of a link.
        // Answered from the row alone — the sweep writes `removedOn` in the
        // same pass that deletes the object, so the row is the record.
        // `datePattern` is the studio's short date, for the date in the
        // sentence (#589); the caller hands it in, as it has the locale.
        isRemoved,
        removedMessage: (row, datePattern) => (row?.removedOn ? removedOn(row.removedOn, datePattern) : null),
      }), [
        files, folders, fileRead.hasRead, folderRead.hasRead, fileRead.failed, folderRead.failed, uploadFile, removeFile, renameFile, moveFile,
        createFolder, renameFolder, moveFolder, removeFolder, links,
      ])}
    >
      {children}
    </FilesContext.Provider>
  );
}

export function useFiles() {
  const ctx = useContext(FilesContext);
  if (!ctx) throw new Error('useFiles must be used within a FilesProvider');
  return ctx;
}

// The library when there is one, and null when there is not — for a lesson
// note's preview (#531), which tests mount on its own with no provider, and
// which then draws each library picture as its name rather than failing.
export function useFilesIfProvided() {
  return useContext(FilesContext);
}
